import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { computeMutualFundMetrics, formatNavDate } from '@/lib/mutualFundMetrics';
import type { MutualFundMetadata } from '@/types/mutualFund';
import { cn } from '@/lib/utils';

interface MutualFundMetricsCardProps {
  metadata: MutualFundMetadata | null | undefined;
  currency: string;
  baseCurrency: string;
  convertValue: (amount: number) => number;
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function MetricTile({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold', className)}>{value}</div>
      </CardContent>
    </Card>
  );
}

const RISK_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive'> = {
  low: 'secondary',
  moderate: 'default',
  high: 'destructive',
};

export function MutualFundMetricsCard({
  metadata,
  currency,
  baseCurrency,
  convertValue,
}: MutualFundMetricsCardProps) {
  if (!metadata) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-muted-foreground">
          No fund metadata available
        </CardContent>
      </Card>
    );
  }

  const metrics = computeMutualFundMetrics(metadata);
  const gainColor = metrics.returnPercent >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div
      className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      role="region"
      aria-label="Mutual fund metrics"
    >
      <MetricTile label="Current Units" value={metadata.units_held.toLocaleString()} />

      <MetricTile
        label="Average Unit Cost"
        value={formatCurrency(convertValue(metadata.unit_cost_basis), currency)}
      />

      <MetricTile
        label="NAV / Bid Price"
        value={formatCurrency(convertValue(metadata.current_nav), currency)}
      />

      <MetricTile label="Available Units" value={metadata.units_held.toLocaleString()} />

      <MetricTile
        label="Unrealized Gain/Loss %"
        value={`${metrics.returnPercent.toFixed(2)}%`}
        className={gainColor}
      />

      <MetricTile
        label="Unrealized Gain/Loss"
        value={formatCurrency(convertValue(metrics.unrealizedGain), currency)}
        className={gainColor}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Risk Level</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant={RISK_BADGE_VARIANT[metadata.risk_rating] ?? 'default'}>
            {metadata.risk_rating.charAt(0).toUpperCase() + metadata.risk_rating.slice(1)}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">NAV Date</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">
            {metadata.nav_date ? formatNavDate(metadata.nav_date) : '—'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
